import React, { useEffect, useState } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import { Check, X, Clock, FileText, Image as ImageIcon, Phone, Home } from "lucide-react";

const STATUS_BADGES = {
  pending: { label: "Pending", bg: "bg-sun text-ink" },
  under_review: { label: "Under Review", bg: "bg-brand-blue text-white" },
  approved: { label: "Approved", bg: "bg-brand-green text-white" },
  rejected: { label: "Rejected", bg: "bg-ink text-white" },
};

export default function NgoFosterApps() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("");
  const [reviewing, setReviewing] = useState(null);

  const load = () => axios.get(`${API}/partner/ngo/foster-applications${filter ? `?status=${filter}` : ""}`).then(r => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, [filter]);

  if (user?.role !== "ngo") return (
    <div className="bg-surface border border-line rounded-3xl p-8 text-center">
      <h2 className="font-heading text-2xl font-extrabold">NGO account required</h2>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="ngo-foster-apps-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">My NGO · Foster Pipeline</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">Foster applications</h1>
        <p className="text-ink-soft text-sm">Review fosters who want to open their homes. Approve to instantly notify them.</p>
      </header>
      <div className="flex flex-wrap gap-2">
        {["", "pending", "under_review", "approved", "rejected"].map(s => (
          <button key={s || "all"} data-testid={`fapp-filter-${s || "all"}`} onClick={() => setFilter(s)} className={`px-4 py-1.5 rounded-full text-xs font-bold border ${filter === s ? "bg-ink text-white border-ink" : "bg-surface border-line text-ink-soft"}`}>
            {s ? STATUS_BADGES[s].label : "All"}
          </button>
        ))}
      </div>
      <div className="space-y-3">
        {items.map(a => (
          <article key={a.application_id} data-testid={`fapp-${a.application_id}`} className="bg-surface border border-line rounded-2xl p-4 flex items-start gap-4 flex-wrap">
            {a.applicant_picture
              ? <img src={a.applicant_picture} alt={a.applicant_name} className="w-12 h-12 rounded-full object-cover"/>
              : <div className="w-12 h-12 rounded-full bg-leaf grid place-items-center font-bold">{a.applicant_name?.[0]}</div>
            }
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2">
                <h3 className="font-heading text-lg font-extrabold">{a.applicant_name}</h3>
                <span className={`${STATUS_BADGES[a.status]?.bg} rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>{STATUS_BADGES[a.status]?.label}</span>
              </div>
              <div className="text-xs text-ink-soft mt-0.5">
                For: <span className="font-bold">{a.listing?.pet_name || "—"}</span> · {a.days_available} days available
                {a.home_type && <> · <Home className="inline w-3 h-3"/> {a.home_type}</>}
                {a.has_other_pets && <> · has other pets</>}
              </div>
              <p className="text-sm text-ink-soft mt-2 line-clamp-2">"{a.message}"</p>
              <div className="flex gap-3 mt-2 text-xs text-ink-soft">
                {a.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3"/>{a.phone}</span>}
                {a.id_proof_url && <span className="inline-flex items-center gap-1 text-brand"><FileText className="w-3 h-3"/>ID uploaded</span>}
                {a.home_photo_url && <span className="inline-flex items-center gap-1 text-brand"><ImageIcon className="w-3 h-3"/>Home photo</span>}
              </div>
            </div>
            <div className="flex gap-2">
              {a.status !== "approved" && (
                <button data-testid={`approve-${a.application_id}`} onClick={() => setReviewing({ a, decision: "approve" })} className="bg-brand-green text-white rounded-full px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
                  <Check className="w-3 h-3"/> Approve
                </button>
              )}
              {a.status === "pending" && (
                <button onClick={() => setReviewing({ a, decision: "under_review" })} className="bg-brand-blue text-white rounded-full px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
                  <Clock className="w-3 h-3"/> Review
                </button>
              )}
              {a.status !== "rejected" && a.status !== "approved" && (
                <button data-testid={`reject-${a.application_id}`} onClick={() => setReviewing({ a, decision: "reject" })} className="bg-surface-muted text-ink rounded-full px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
                  <X className="w-3 h-3"/> Reject
                </button>
              )}
            </div>
          </article>
        ))}
      </div>
      {items.length === 0 && (
        <div className="bg-surface border border-dashed border-line rounded-3xl p-10 text-center text-ink-soft">No foster applications yet.</div>
      )}
      {reviewing && <ReviewModal {...reviewing} onClose={() => { setReviewing(null); load(); }}/>}
    </div>
  );
}

function ReviewModal({ a, decision, onClose }) {
  const [note, setNote] = useState("");
  const submit = async () => {
    await axios.patch(`${API}/partner/ngo/foster-applications/${a.application_id}`, { decision, review_note: note });
    onClose();
  };
  const verb = decision === "approve" ? "Approve" : decision === "reject" ? "Reject" : "Mark under review";
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-surface rounded-3xl p-6 w-full max-w-md" data-testid="review-modal">
        <h3 className="font-heading text-xl font-extrabold">{verb} application</h3>
        <p className="text-sm text-ink-soft mt-1">By <span className="font-bold">{a.applicant_name}</span> for <span className="font-bold">{a.listing?.pet_name}</span></p>
        <label className="block mt-4">
          <span className="text-xs uppercase tracking-wider font-bold text-ink-soft">Note (optional)</span>
          <textarea data-testid="review-note" value={note} onChange={e => setNote(e.target.value)} rows={3} className="w-full mt-1 bg-surface-muted border border-line rounded-2xl p-3 outline-none focus:border-brand"/>
        </label>
        <div className="flex gap-2 mt-4">
          <button onClick={onClose} className="flex-1 bg-surface-muted rounded-full py-2.5 font-bold">Cancel</button>
          <button data-testid="confirm-review" onClick={submit} className="flex-1 bg-brand text-white rounded-full py-2.5 font-bold">{verb}</button>
        </div>
      </div>
    </div>
  );
}
